import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useNotifications } from '../context/NotificationContext'
import { useTheme } from '../context/ThemeContext'
import { useAuthStore } from '../store/authStore'
import { Bell, Check, Trash2, X, RotateCw } from 'lucide-react'

export default function NotificationBell({ darkMode: propDarkMode }) {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, clearAllNotifications, fetchNotifications } = useNotifications()
  const { darkMode: contextDarkMode } = useTheme()
  const isDark = propDarkMode !== undefined ? propDarkMode : contextDarkMode
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false)
    }
    const handleKeyDown = (e) => { if (e.key === 'Escape') setIsOpen(false) }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') return notifications.filter((n) => !n.is_read)
    if (activeTab === 'alerts') return notifications.filter((n) => n.type === 'warning' || n.type === 'error' || n.type === 'alert')
    return notifications
  }, [notifications, activeTab])

  const alertsCount = useMemo(() => notifications.filter((n) => n.type === 'warning' || n.type === 'error' || n.type === 'alert').length, [notifications])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try { if (fetchNotifications) await fetchNotifications() } finally { setTimeout(() => setIsRefreshing(false), 500) }
  }

  const handleNotificationClick = async (notification) => {
    if (notification.id && !notification.is_read) await markAsRead(notification.id)
    if (notification.link) {
      setIsOpen(false)
      if (notification.link.startsWith('http')) window.open(notification.link, '_blank', 'noopener,noreferrer')
      else navigate(notification.link)
    }
  }

  const handleDelete = async (e, id) => {
    e.stopPropagation()
    setDeletingId(id)
    try { if (deleteNotification) await deleteNotification(id) } finally { setDeletingId(null) }
  }

  const handleMarkRead = async (e, id) => { e.stopPropagation(); await markAsRead(id) }

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return 'Just now'
    const date = new Date(dateStr)
    const diffMs = Date.now() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHrs = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHrs / 24)
    if (diffMs < 45000) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHrs < 24) return `${diffHrs}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  const messagesPath = user?.user_type === 'student' ? '/student/messages' : user?.user_type === 'admin' ? '/admin/communications' : null

  const tabBtn = (tab, label, count) => (
    <button
      type="button"
      onClick={() => setActiveTab(tab)}
      className={`px-2.5 py-1 text-xs transition-colors ${activeTab === tab ? (isDark ? 'text-white font-medium' : 'text-gray-900 font-medium') : (isDark ? 'text-cinema-muted hover:text-white' : 'text-gray-500 hover:text-gray-900')}`}
    >
      {label}{count > 0 && <span className="ml-1.5 text-cinema-glow">{count}</span>}
    </button>
  )

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`relative flex items-center justify-center w-9 h-9 transition-colors ${isDark ? 'text-cinema-soft hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        title="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Bell className="w-[18px] h-[18px]" />
        {unreadCount > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full ${isDark ? 'bg-cinema-glow text-black' : 'bg-cinema-glow text-black'}`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Mobile backdrop */}
      {isOpen && <div className="fixed inset-0 z-40 bg-black/30 sm:hidden" onClick={() => setIsOpen(false)} aria-hidden="true" />}

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`fixed left-1/2 -translate-x-1/2 top-14 sm:absolute sm:top-full sm:left-auto sm:right-0 sm:translate-x-0 sm:mt-1 w-[calc(100vw-24px)] max-w-[400px] sm:w-[400px] border z-50 flex flex-col ${isDark ? 'bg-[#0d0d10] border-white/10' : 'bg-white border-gray-200'}`}
          style={{ colorScheme: isDark ? 'dark' : 'light' }}
        >
          {/* Header */}
          <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
            <div className="flex items-center gap-2">
              <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Notifications</h3>
              {unreadCount > 0 && <span className="text-xs text-cinema-glow">{unreadCount} new</span>}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={handleRefresh} className={`p-1 transition-colors ${isDark ? 'text-cinema-muted hover:text-white' : 'text-gray-400 hover:text-gray-700'}`} title="Refresh">
                <RotateCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
              {unreadCount > 0 && (
                <button type="button" onClick={() => markAllAsRead()} className={`text-xs transition-colors ${isDark ? 'text-cinema-gold hover:text-cinema-glow' : 'text-primary-600 hover:text-primary-700'}`}>
                  Mark all read
                </button>
              )}
              <button type="button" onClick={() => setIsOpen(false)} className={`p-1 transition-colors ${isDark ? 'text-cinema-muted hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className={`flex items-center gap-1 px-4 py-1.5 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
            {tabBtn('all', 'All', notifications.length)}
            {tabBtn('unread', 'Unread', unreadCount)}
            {alertsCount > 0 && tabBtn('alerts', 'Alerts', alertsCount)}
          </div>

          {/* List */}
          <div className={`overflow-y-auto max-h-[380px] divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
            {filteredNotifications.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <p className={`text-sm ${isDark ? 'text-cinema-muted' : 'text-gray-500'}`}>
                  {activeTab === 'unread' ? 'All caught up.' : activeTab === 'alerts' ? 'No active alerts.' : 'No notifications.'}
                </p>
              </div>
            ) : (
              filteredNotifications.map((notification, index) => {
                const isUnread = !notification.is_read
                return (
                  <div
                    key={notification.id || `notif-${index}`}
                    onClick={() => handleNotificationClick(notification)}
                    className={`group relative p-3.5 cursor-pointer transition-colors ${isUnread ? (isDark ? 'bg-white/[0.02]' : 'bg-gray-50/50') : ''} ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'} ${deletingId === notification.id ? 'opacity-40' : ''}`}
                  >
                    {isUnread && <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-cinema-glow" />}
                    <div className="flex items-start gap-2.5 pl-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className={`text-sm font-medium truncate ${isDark ? (isUnread ? 'text-white' : 'text-cinema-soft') : (isUnread ? 'text-gray-900' : 'text-gray-700')}`}>
                            {notification.title}
                          </h4>
                          <span className={`text-[11px] shrink-0 ${isDark ? 'text-cinema-muted' : 'text-gray-400'}`}>
                            {getTimeAgo(notification.created_at)}
                          </span>
                        </div>
                        <p className={`text-xs mt-1 leading-relaxed line-clamp-2 ${isDark ? 'text-cinema-muted' : 'text-gray-500'}`}>
                          {notification.message}
                        </p>
                      </div>
                      {/* Actions on hover */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        {isUnread && (
                          <button type="button" onClick={(e) => handleMarkRead(e, notification.id)} className={`p-1 transition-colors ${isDark ? 'text-cinema-muted hover:text-white' : 'text-gray-400 hover:text-gray-800'}`} title="Mark as read">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button type="button" onClick={(e) => handleDelete(e, notification.id)} className={`p-1 transition-colors ${isDark ? 'text-cinema-muted hover:text-red-400' : 'text-gray-400 hover:text-red-600'}`} title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Footer */}
          <div className={`px-4 py-2.5 border-t flex items-center justify-between text-xs ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
            {notifications.length > 0 ? (
              <button type="button" onClick={() => { if (window.confirm('Clear all notifications?')) clearAllNotifications() }} className={`transition-colors ${isDark ? 'text-cinema-muted hover:text-red-400' : 'text-gray-500 hover:text-red-600'}`}>
                Clear all
              </button>
            ) : (
              <span className={isDark ? 'text-cinema-muted' : 'text-gray-400'}>No active notifications</span>
            )}
            {messagesPath && (
              <Link to={messagesPath} onClick={() => setIsOpen(false)} className={`transition-colors ${isDark ? 'text-cinema-gold hover:text-cinema-glow' : 'text-primary-600 hover:text-primary-700'}`}>
                Message center →
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
